/**
 * Shared effect plumbing for the effectful-hero styles. Every portable style is ported to a
 * headless "core" (`EffectXxxCore.ts`) that owns one canvas and exposes
 * `EffectInstance`; the worker (`EffectWorker.ts`) drives a core on the canvas thread
 * (`EffectEffectStage.client.tsx` only posts ticks), and the React fallback
 * (`EffectFxHost.client.tsx`) drives the same core on the main thread for browsers
 * without OffscreenCanvas. `aero-shards` and `shape-waves` stay on their React
 * components: WebGPU is not callable inside workers on Firefox/Safari, and `vgpu`
 * assumes a document.
 *
 * No DOM is touched here, so the module is safe to import from a worker.
 */

export type EffectPass = {
  width: number;
  height: number;
  /** `window.devicePixelRatio` as seen by the host; cores apply their own caps. */
  dpr: number;
  /** `window.innerWidth`/`innerHeight`; only Prism reads them (its hover is window-relative). */
  vw: number;
  vh: number;
  reduceMotion: boolean;
};

export type EffectInstance = {
  resize: (width: number, height: number, dpr: number) => void;
  /** Canvas-local CSS pixels plus the raw client coords (Prism normalizes over the window). */
  pointer?: (x: number, y: number, cx: number, cy: number) => void;
  pointerOut?: () => void;
  /** Pointer press used for one-shot impulses (PatternWaves ripple burst). */
  burst?: (x: number, y: number) => void;
  /** One animation step; the host pumps this every frame and simply stops while hidden. */
  frame: (now: number) => void;
  dispose: () => void;
};

export type EffectFactory = (
  canvas: HTMLCanvasElement | OffscreenCanvas,
  pass: EffectPass,
) => EffectInstance;

/** Styles whose core runs on the canvas thread. The rest keep their React component. */
export const WORKER_ANIMATIONS = [
  "aurora",
  "waves",
  "dot-field",
  "dither",
  "beams",
  "gradient-blinds",
  "line-waves",
  "prism",
  "lightfall",
  "pattern-waves",
  "gradient-waves",
] as const;

/** The subset of styles that have a core and can therefore run in a worker. */
export type EffectWorkerAnimation = (typeof WORKER_ANIMATIONS)[number];

const WORKER_SET: ReadonlySet<string> = new Set(WORKER_ANIMATIONS);

/** Styles without a core (the vgpu pair) only exist as React components in the hero. */
export const isWorkerAnimation = (value: string): value is EffectWorkerAnimation =>
  WORKER_SET.has(value);

export const supportsEffectWorker = (canvas: HTMLCanvasElement): boolean =>
  typeof Worker === "function" &&
  typeof OffscreenCanvas === "function" &&
  typeof canvas.transferControlToOffscreen === "function";

/**
 * A 2D context used for inline raster work (color parsing, glyph atlas). The host side
 * has a document; the worker side does not, so OffscreenCanvas is used there.
 */
export const probe2d = (): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null => {
  try {
    if (typeof document !== "undefined") {
      return document.createElement("canvas").getContext("2d", { willReadFrequently: true });
    }
    return new OffscreenCanvas(1, 1).getContext("2d", { willReadFrequently: true });
  } catch {
    return null;
  }
};

/** A detached 2D canvas for atlas / mask rasterization, working in both directions. */
export const makeCanvas2d = (
  width: number,
  height: number,
): {
  canvas: HTMLCanvasElement | OffscreenCanvas;
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
} | null => {
  const canvas =
    typeof document !== "undefined"
      ? document.createElement("canvas")
      : new OffscreenCanvas(width, height);
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  return { canvas, ctx };
};
